import { useState, useEffect, useMemo } from 'react';
import { Node, Edge } from '@xyflow/react';
import {
  TextInput,
  TextArea,
  Button,
  Tag,
  Select,
  SelectItem,
  IconButton,
  Toggle,
} from '@carbon/react';
import {
  Save,
  Add,
  Close,
  TrashCan,
  ArrowRight,
  ArrowsHorizontal,
} from '@carbon/icons-react';
import {
  ConceptEdgeData,
  EdgeProperty,
  RELATIONSHIP_TYPE_OPTIONS,
  createDefaultEdgeData,
} from './edge-types';
import { ConceptNodeData } from './ConceptNode';
import { ProvenanceSection } from './ProvenanceSection';

interface EdgeDetailsPanelProps {
  edge: Edge;
  sourceNode?: Node;
  targetNode?: Node;
  onUpdateEdge: (edgeId: string, data: Partial<ConceptEdgeData>) => void;
  onDeleteEdge: (edgeId: string) => void;
  onClose?: () => void;
  isReadOnly?: boolean;
}

/**
 * EdgeDetailsPanel — symmetric to NodeDetailsPanel but for relationships.
 * Allows the user to edit a link's label, type, description, properties,
 * direction, and to delete the relationship.
 */
export const EdgeDetailsPanel = ({
  edge,
  sourceNode,
  targetNode,
  onUpdateEdge,
  onDeleteEdge,
  onClose,
  isReadOnly = false,
}: EdgeDetailsPanelProps) => {
  const initial = useMemo<ConceptEdgeData>(() => {
    const d = (edge.data ?? {}) as Partial<ConceptEdgeData>;
    return createDefaultEdgeData({
      ...d,
      // Fall back to edge.label if data.label is missing (legacy edges)
      label:
        d.label ??
        (typeof edge.label === 'string' ? edge.label : undefined) ??
        'relates to',
    });
  }, [edge]);

  const [label, setLabel] = useState(initial.label);
  const [relationshipType, setRelationshipType] = useState<string>(
    initial.relationshipType || 'relates-to',
  );
  const [description, setDescription] = useState(initial.description || '');
  const [properties, setProperties] = useState<EdgeProperty[]>(initial.properties);
  const [bidirectional, setBidirectional] = useState<boolean>(!!initial.bidirectional);

  const [newPropertyName, setNewPropertyName] = useState('');
  const [newPropertyType, setNewPropertyType] = useState('');

  // Reset when the selected edge changes
  useEffect(() => {
    setLabel(initial.label);
    setRelationshipType(initial.relationshipType || 'relates-to');
    setDescription(initial.description || '');
    setProperties(initial.properties);
    setBidirectional(!!initial.bidirectional);
  }, [edge.id, initial]);

  const handleSave = () => {
    onUpdateEdge(edge.id, {
      label: label.trim() || 'relates to',
      relationshipType,
      description,
      properties,
      bidirectional,
    });
  };

  const handleDelete = () => {
    onDeleteEdge(edge.id);
  };

  const addProperty = () => {
    if (newPropertyName.trim()) {
      const next: EdgeProperty = newPropertyType.trim()
        ? { name: newPropertyName.trim(), type: newPropertyType.trim() }
        : { name: newPropertyName.trim() };
      setProperties([...properties, next]);
      setNewPropertyName('');
      setNewPropertyType('');
    }
  };

  const removeProperty = (index: number) => {
    setProperties(properties.filter((_, i) => i !== index));
  };

  const hasChanges =
    label !== initial.label ||
    relationshipType !== (initial.relationshipType || 'relates-to') ||
    description !== (initial.description || '') ||
    JSON.stringify(properties) !== JSON.stringify(initial.properties) ||
    bidirectional !== !!initial.bidirectional;

  // Apply a relationship type from the dropdown — also auto-fill the label
  // if the user hasn't customised it (i.e. label still matches the previous
  // canonical type's display string). This keeps things ergonomic without
  // overwriting user-typed labels.
  const handleRelationshipTypeChange = (newType: string) => {
    const oldOpt = RELATIONSHIP_TYPE_OPTIONS.find((o) => o.value === relationshipType);
    const newOpt = RELATIONSHIP_TYPE_OPTIONS.find((o) => o.value === newType);
    if (newOpt && oldOpt && (label === oldOpt.label || label === oldOpt.value)) {
      setLabel(newOpt.label);
    } else if (newOpt && (!label || label === 'relates to')) {
      setLabel(newOpt.label);
    }
    setRelationshipType(newType);
  };

  const sourceLabel =
    (sourceNode?.data as ConceptNodeData | undefined)?.label || edge.source;
  const targetLabel =
    (targetNode?.data as ConceptNodeData | undefined)?.label || edge.target;

  return (
    <div className="node-details-panel edge-details-panel">
      {/* Header */}
      <div className="node-details-panel__header">
        <div className="node-details-panel__header-left">
          <div className="node-details-panel__node-indicator edge-details-panel__indicator" />
          <span className="node-details-panel__title">Relationship</span>
        </div>
        {onClose && (
          <IconButton
            kind="ghost"
            size="sm"
            label="Close inspector"
            align="bottom-right"
            onClick={onClose}
            className="node-details-panel__close"
          >
            <Close size={16} />
          </IconButton>
        )}
      </div>

      {/* Source → Target preview */}
      <div className="edge-details-panel__preview">
        <Tag type="blue" size="sm" title={String(sourceLabel)}>
          {String(sourceLabel)}
        </Tag>
        {bidirectional ? (
          <ArrowsHorizontal size={16} />
        ) : (
          <ArrowRight size={16} />
        )}
        <Tag type="green" size="sm" title={String(targetLabel)}>
          {String(targetLabel)}
        </Tag>
      </div>

      {/* A10: provenance — same traceability as node inspector. */}
      <ProvenanceSection
        provenance={initial.provenance}
        createdByName={initial.createdByName}
        updatedByName={initial.updatedByName}
        correctedAt={initial.correctedAt}
        testId="edge-provenance"
      />

      {/* AI rationale — edge-specific, shown below the provenance section. */}
      {initial.rationale ? (
        <div style={{ padding: '0.5rem 1rem 0', fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>
          <p data-testid="edge-rationale">
            <strong>Why the AI related these:</strong> {initial.rationale}
          </p>
        </div>
      ) : null}

      {/* Form */}
      <div className="node-details-panel__form">
        {/* Label */}
        <div className="node-details-panel__field">
          <TextInput
            id="edge-label"
            labelText="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. uses, depends on..."
            size="sm"
            readOnly={isReadOnly}
          />
        </div>

        {/* Relationship type */}
        <div className="node-details-panel__field">
          <Select
            id="edge-relationship-type"
            labelText="Relationship type"
            value={relationshipType}
            onChange={(e) => handleRelationshipTypeChange(e.target.value)}
            size="sm"
            disabled={isReadOnly}
          >
            {RELATIONSHIP_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} text={opt.label} />
            ))}
          </Select>
        </div>

        {/* Description */}
        <div className="node-details-panel__field node-details-panel__field--definition">
          <TextArea
            id="edge-description"
            labelText="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this relationship..."
            rows={6}
            readOnly={isReadOnly}
          />
        </div>

        {/* Bidirectional toggle */}
        <div className="node-details-panel__field edge-details-panel__toggle-row">
          <Toggle
            id="edge-bidirectional"
            size="sm"
            labelText="Bidirectional"
            labelA="Off"
            labelB="On"
            toggled={bidirectional}
            onToggle={(checked: boolean) => setBidirectional(checked)}
            disabled={isReadOnly}
          />
        </div>

        {/* Properties */}
        <div className="node-details-panel__field">
          <div className="node-details-panel__section-header">
            <span className="node-details-panel__section-label">Properties</span>
            <span className="node-details-panel__section-count">
              {properties.length}
            </span>
          </div>

          {properties.length > 0 && (
            <div className="node-details-panel__props-list">
              {properties.map((property, index) => (
                <Tag
                  key={index}
                  type="cool-gray"
                  size="sm"
                  filter={!isReadOnly}
                  onClose={isReadOnly ? undefined : () => removeProperty(index)}
                  title={
                    property.type
                      ? `${property.name}: ${property.type}`
                      : property.name
                  }
                >
                  {property.name}
                  {property.type && (
                    <span className="node-details-panel__prop-type">
                      :{property.type}
                    </span>
                  )}
                </Tag>
              ))}
            </div>
          )}

          {!isReadOnly && (
            <div className="node-details-panel__add-prop">
              <TextInput
                id="new-edge-prop-name"
                labelText=""
                hideLabel
                value={newPropertyName}
                onChange={(e) => setNewPropertyName(e.target.value)}
                placeholder="New property..."
                size="sm"
                onKeyDown={(e) => e.key === 'Enter' && addProperty()}
              />
              <Select
                id="new-edge-prop-type"
                labelText=""
                hideLabel
                value={newPropertyType}
                onChange={(e) => setNewPropertyType(e.target.value)}
                size="sm"
              >
                <SelectItem value="" text="Type" />
                <SelectItem value="str" text="str" />
                <SelectItem value="int" text="int" />
                <SelectItem value="float" text="float" />
                <SelectItem value="bool" text="bool" />
                <SelectItem value="list" text="list" />
                <SelectItem value="dict" text="dict" />
                <SelectItem value="datetime" text="date" />
              </Select>
              <IconButton
                kind="ghost"
                size="sm"
                label="Add property"
                align="left"
                onClick={addProperty}
                disabled={!newPropertyName.trim()}
              >
                <Add size={16} />
              </IconButton>
            </div>
          )}
        </div>
      </div>

      {/* Footer — hidden in read-only mode */}
      {!isReadOnly && (
        <div className="node-details-panel__footer edge-details-panel__footer">
          <Button
            kind="danger--ghost"
            size="sm"
            renderIcon={TrashCan}
            onClick={handleDelete}
            className="edge-details-panel__delete-btn"
          >
            Delete
          </Button>
          <Button
            kind="primary"
            size="sm"
            renderIcon={Save}
            onClick={handleSave}
            disabled={!hasChanges}
            className="node-details-panel__save-btn"
          >
            {hasChanges ? 'Save Changes' : 'Saved'}
          </Button>
        </div>
      )}
    </div>
  );
};

// Made with Bob
